import React, { useContext } from 'react';
import { FiFile, FiUser, FiFolder, FiBox, FiGrid, FiClock, FiTag } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Context from '../../context/Context';

const SearchResults = ({ results, isLoading, query }) => {
  const { user } = useContext(Context);
  if (isLoading) {
    return (
      <div className="p-4 flex justify-center items-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const hasResults = results && (
    results.users?.data?.length ||
    results.folders?.data?.length ||
    results.sheets?.data?.length ||
    results.sheet_companies?.data?.length ||
    results.categories?.data?.length ||
    results.units?.data?.length ||
    results.timeframes?.data?.length
  );

  if (!hasResults) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        {query ? `No results found for "${query}"` : 'Start typing to search...'}
      </div>
    );
  }

  const renderSection = (title, items, icon, path, nameKey = 'name', type = null) => {
    if (!items || items.length === 0) return null;

    return (
      <div className="mb-1">
        <h3 className="text-xs font-semibold text-gray-400 uppercase px-4 py-1">{title}</h3>
        <ul className='!pl-0 '>
          {items.map(item => (
            <li key={`${title}-${item.id}`}>
              <Link
                to={path}
                state={{
                  searchQuery: item[nameKey],
                  ...(path === '/personal/drive' && type && { highlightType: type })
                }}
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 no-underline text-gray-800"
              >
                {icon}
                <span className='text-sm'>{item[nameKey]}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderUsersSection = (title, items, icon, path) => {
    if (!items || items.length === 0) return null;

    return (
      <div className="mb-1">
        <h3 className="text-xs font-semibold text-gray-400 uppercase px-4 py-1">{title}</h3>
        <ul className='!pl-0'>
          {items.map(item => (
            <li key={`user-${item.user_id}`}>
              <Link
                to={path}
                state={{
                  searchQuery: `${item.first_name} ${item.last_name}`,
                }}
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 no-underline text-gray-800"
              >
                {icon}
                <span className='text-sm'>{`${item.first_name} ${item.last_name}`}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const renderSheetsSection = (title, items, icon) => {
    if (!items || items.length === 0) return null;

    const isPersonalMode = user?.role === 'Personal User';

    return (
      <div className="mb-1">
        <h3 className="text-xs font-semibold text-gray-400 uppercase px-4 py-1">{title}</h3>
        <ul className='!pl-0'>
          {items.map(item => {
            let path = '/personal/drive'; // Default for business mode
            if (isPersonalMode) {
              path = item.is_shared ? '/dashboard/shared' : '/dashboard/personal';
            }

            return (
              <li key={`${title}-${item.id}`}>
                <Link
                  to={path}
                  state={{ searchQuery: item.title }}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 no-underline text-gray-800"
                >
                  {icon}
                  <span className='text-sm'>{item.title}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const getPathForSection = (section) => {
    const isPersonal = user?.role === 'Personal User';
    switch (section) {
      case 'Sheet Companies':
        return isPersonal ? '/company' : '/entries?tab=Companies';
      case 'Categories':
        return isPersonal ? '/category' : '/entries?tab=Categories';
      case 'Units':
        return isPersonal ? '/units' : '/entries?tab=Units';
      case 'Timeframes':
        return isPersonal ? '/time-period' : '/entries?tab=Time Period';
      default:
        return '/';
    }
  };

  return (
    <div className="py-2">
      {renderUsersSection('Users', results.users?.data, <FiUser className="h-4 w-4 text-gray-500" />, '/company/employees')}
      {renderSection('Folders', results.folders?.data, <FiFolder className="h-4 w-4 text-gray-500" />, '/personal/drive', 'name', 'folder')}
      {renderSheetsSection('Sheets', results.sheets?.data, <FiFile className="h-4 w-4 text-gray-500" />)}
      {renderSection('Sheet Companies', results.sheet_companies?.data, <FiBox className="h-4 w-4 text-gray-500" />, getPathForSection('Sheet Companies'), 'sheet_company_name')}
      {renderSection('Categories', results.categories?.data, <FiGrid className="h-4 w-4 text-gray-500" />, getPathForSection('Categories'), 'category_name')}
      {renderSection('Units', results.units?.data, <FiTag className="h-4 w-4 text-gray-500" />, getPathForSection('Units'), 'name')}
      {renderSection('Timeframes', results.timeframes?.data, <FiClock className="h-4 w-4 text-gray-500" />, getPathForSection('Timeframes'), 'year')}
    </div>
  );
};

export default SearchResults;